
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit2, Trash2, Power, Share2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

interface MealCardProps {
  meal: {
    id: string;
    name: string;
    price: number;
    image: string;
    description: string;
    status: string;
    cuisine: string;
    dietary?: string[];
    ingredients?: string[];
    allergens?: string[];
    preOrder?: boolean;
    scheduledDate?: string;
    availablePortions?: number;
    preparationTime?: string;
  };
  onDelete: () => void;
  onToggleStatus: () => void;
  onEdit: () => void;
}

export const MealCard = ({ meal, onDelete, onToggleStatus }: MealCardProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const formatDate = (dateString: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleShare = async () => {
    try {
      // Universal link format that works for both web and mobile
      const webUrl = `${window.location.origin}/meal/${meal.id}`;
      const deepLink = `homeplate://meal/${meal.id}`;
      
      // Create the share text with both URLs
      const shareText = `Check out ${meal.name} on HomePlate!\n\n` +
                       `Open in app: ${deepLink}\n` +
                       `Web version: ${webUrl}`;

      // Try to use clipboard API first since we're in an iframe
      await navigator.clipboard.writeText(shareText);
      toast({
        title: "Success",
        description: "Share link copied to clipboard! The link will open in the HomePlate app if installed.",
      });
    } catch (error) {
      console.error('Share error:', error);
      
      toast({
        title: "Error",
        description: "Could not copy link to clipboard. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Card className="overflow-hidden">
        <div className="relative h-48">
          <img
            src={meal.image}
            alt={meal.name}
            className="w-full h-full object-cover"
          />
          <Badge 
            className={`absolute top-4 right-4 ${
              meal.status === "active" ? "bg-green-500" : "bg-gray-500"
            }`}
          >
            {meal.status === "active" ? "Active" : "Inactive"}
          </Badge>
        </div>
        
        <div className="p-4">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="font-semibold text-lg">{meal.name}</h3>
              <p className="text-sm text-muted-foreground">${meal.price.toFixed(2)}</p>
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {meal.description}
          </p>

          {meal.cuisine && (
            <Badge variant="outline" className="mb-2">
              {meal.cuisine}
            </Badge>
          )}

          {meal.dietary && meal.dietary.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {meal.dietary.map((tag, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {meal.preOrder && (
            <div className="mb-4">
              <Badge variant="secondary">Pre-order</Badge>
              <p className="text-sm text-muted-foreground mt-1">
                Available: {meal.scheduledDate && formatDate(meal.scheduledDate)}
              </p>
              <p className="text-sm text-muted-foreground">
                Portions: {meal.availablePortions}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleShare}
              className="w-full"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Meal Link
            </Button>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={() => navigate(`/seller/meals/edit/${meal.id}`)}
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={onToggleStatus}
                className="flex-1"
              >
                <Power className="w-4 h-4 mr-2" />
                {meal.status === "active" ? "Deactivate" : "Activate"}
              </Button>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your meal listing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
